/**
 * DraftSeatOverlay - Renders LOCAL unsaved seat drafts + live draw preview.
 * Elvis Aloha Collection - Map Editor
 *
 * Uses the SAME full-image SVG coordinate system as canonical seat rendering:
 * a single SVG whose viewBox equals the native chart dimensions, attached to
 * the OpenSeadragon tiled image so it transforms (pan/zoom) together with the
 * chart. All rectangles use native x/y/width/height (no CSS screen coords),
 * guaranteeing zero drift on pan, zoom, sidebar collapse, fullscreen or reload.
 *
 * This layer is purely visual. Pointer interaction (draw / move / resize) is
 * handled by the interaction layer in MapEditorStudio and converted to native
 * coordinates there; this component just paints the current state.
 */

import { useEffect, useRef, useCallback } from 'react';
import OpenSeadragon from 'openseadragon';

const SVG_NS = 'http://www.w3.org/2000/svg';

// 8 resize handles + rotation handle, returned in NATIVE coordinates.
export const getDraftHandles = (draft) => {
  const w = draft.width, h = draft.height;
  const left = draft.native_x - w / 2;
  const top = draft.native_y - h / 2;
  const right = left + w;
  const bottom = top + h;
  const midX = draft.native_x;
  const midY = draft.native_y;
  return {
    nw: { x: left, y: top },
    n: { x: midX, y: top },
    ne: { x: right, y: top },
    e: { x: right, y: midY },
    se: { x: right, y: bottom },
    s: { x: midX, y: bottom },
    sw: { x: left, y: bottom },
    w: { x: left, y: midY },
    rotate: { x: midX, y: top - Math.max(24, h * 0.9) },
  };
};

const DraftSeatOverlay = ({ viewer, chartDimensions, drafts = [], selectedId, preview, scale = 1 }) => {
  const overlayRef = useRef(null);
  const svgRef = useRef(null);

  const render = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const handleSize = Math.max(6, 9 / (scale || 1));
    const strokeW = Math.max(1, 1.5 / (scale || 1));

    // Live draw preview (dashed)
    if (preview && preview.width > 0 && preview.height > 0) {
      const p = document.createElementNS(SVG_NS, 'rect');
      p.setAttribute('x', preview.x);
      p.setAttribute('y', preview.y);
      p.setAttribute('width', preview.width);
      p.setAttribute('height', preview.height);
      p.setAttribute('fill', 'rgba(59, 130, 246, 0.25)');
      p.setAttribute('stroke', '#2563EB');
      p.setAttribute('stroke-width', strokeW * 1.5);
      p.setAttribute('stroke-dasharray', `${handleSize * 0.8} ${handleSize * 0.5}`);
      p.style.pointerEvents = 'none';
      p.setAttribute('data-testid', 'draft-draw-preview');
      svg.appendChild(p);
    }

    drafts.forEach((d) => {
      const w = d.width, h = d.height;
      const x = d.native_x - w / 2;
      const y = d.native_y - h / 2;
      const isSel = d.id === selectedId;
      const hasError = !!d.error;

      const g = document.createElementNS(SVG_NS, 'g');
      g.setAttribute('data-draft-id', d.id);
      g.setAttribute('data-testid', `draft-seat-${d.id}`);
      g.setAttribute('transform', `rotate(${d.rotation || 0}, ${d.native_x}, ${d.native_y})`);

      const rect = document.createElementNS(SVG_NS, 'rect');
      rect.setAttribute('x', x);
      rect.setAttribute('y', y);
      rect.setAttribute('width', w);
      rect.setAttribute('height', h);
      rect.setAttribute('rx', Math.min(1.5, w * 0.1));
      rect.setAttribute('fill', hasError ? 'rgba(220, 38, 38, 0.30)' : 'rgba(34, 197, 94, 0.35)');
      rect.setAttribute('stroke', hasError ? '#DC2626' : (isSel ? '#F59E0B' : '#16A34A'));
      rect.setAttribute('stroke-width', isSel ? strokeW * 2 : strokeW);
      rect.setAttribute('stroke-dasharray', `${handleSize * 0.6} ${handleSize * 0.4}`);
      rect.style.pointerEvents = 'none';
      g.appendChild(rect);

      // Label (section/row/seat) if present
      const labelParts = [d.section, d.row && `R${d.row}`, d.seat && `S${d.seat}`].filter(Boolean);
      if (labelParts.length) {
        const t = document.createElementNS(SVG_NS, 'text');
        t.setAttribute('x', d.native_x);
        t.setAttribute('y', y - handleSize * 0.5);
        t.setAttribute('fill', hasError ? '#B91C1C' : '#166534');
        t.setAttribute('font-size', Math.max(9, 11 / (scale || 1)));
        t.setAttribute('font-weight', '700');
        t.setAttribute('text-anchor', 'middle');
        t.style.pointerEvents = 'none';
        t.textContent = labelParts.join(' ');
        g.appendChild(t);
      }

      svg.appendChild(g);

      // Selection handles (drawn un-rotated for simplicity)
      if (isSel) {
        const handles = getDraftHandles(d);
        Object.entries(handles).forEach(([name, pt]) => {
          if (name === 'rotate') {
            const c = document.createElementNS(SVG_NS, 'circle');
            c.setAttribute('cx', pt.x);
            c.setAttribute('cy', pt.y);
            c.setAttribute('r', handleSize * 0.6);
            c.setAttribute('fill', '#F59E0B');
            c.setAttribute('stroke', '#fff');
            c.setAttribute('stroke-width', strokeW);
            c.style.pointerEvents = 'none';
            c.setAttribute('data-handle', 'rotate');
            svg.appendChild(c);
          } else {
            const hs = document.createElementNS(SVG_NS, 'rect');
            hs.setAttribute('x', pt.x - handleSize / 2);
            hs.setAttribute('y', pt.y - handleSize / 2);
            hs.setAttribute('width', handleSize);
            hs.setAttribute('height', handleSize);
            hs.setAttribute('fill', '#fff');
            hs.setAttribute('stroke', '#F59E0B');
            hs.setAttribute('stroke-width', strokeW);
            hs.style.pointerEvents = 'none';
            hs.setAttribute('data-handle', name);
            svg.appendChild(hs);
          }
        });
      }
    });
  }, [drafts, selectedId, preview, scale]);

  const setupOverlay = useCallback(() => {
    if (!viewer || !chartDimensions?.width || !chartDimensions?.height) return;
    const tiledImage = viewer.world.getItemAt(0);
    if (!tiledImage) return;

    if (overlayRef.current) {
      try { viewer.removeOverlay(overlayRef.current); } catch (e) { /* noop */ }
    }

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', `0 0 ${chartDimensions.width} ${chartDimensions.height}`);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.style.position = 'absolute';
    svg.style.top = '0';
    svg.style.left = '0';
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.pointerEvents = 'none';
    svg.setAttribute('data-testid', 'draft-seat-overlay-svg');
    svgRef.current = svg;

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.pointerEvents = 'none';
    container.appendChild(svg);
    overlayRef.current = container;

    const imageRect = tiledImage.imageToViewportRectangle(0, 0, chartDimensions.width, chartDimensions.height);
    viewer.addOverlay({
      element: container,
      location: new OpenSeadragon.Rect(imageRect.x, imageRect.y, imageRect.width, imageRect.height),
    });

    render();
  }, [viewer, chartDimensions, render]);

  useEffect(() => {
    if (!viewer) return;
    const handleOpen = () => { if (viewer.world.getItemAt(0)) setupOverlay(); };
    if (viewer.isOpen && viewer.isOpen()) handleOpen();
    viewer.addHandler('open', handleOpen);
    return () => {
      viewer.removeHandler('open', handleOpen);
      if (overlayRef.current) {
        try { viewer.removeOverlay(overlayRef.current); } catch (e) { /* noop */ }
      }
    };
  }, [viewer, setupOverlay]);

  useEffect(() => { render(); }, [render]);

  return null;
};

export default DraftSeatOverlay;
